import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Message } from '../../types/api';
import { ChartRenderer } from '../chart/ChartRenderer';

export function MessageBubble({
  message,
  onPin,
  onEdit,
  onRegenerate,
  onCopy,
  onDelete,
  pinned,
}: {
  message: Message;
  onPin?: () => void;
  onEdit?: (newContent: string) => void;
  onRegenerate?: () => void;
  onCopy?: () => void;
  onDelete?: () => void;
  pinned?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const [justCopied, setJustCopied] = useState(false);

  const isUser = message.role === 'user';

  function handleCopy(): void {
    onCopy?.();
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 1600);
  }

  return (
    <article id={message.id} className="group" data-role={message.role}>
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[88%] sm:max-w-[78%] rounded-2xl px-4 py-3 text-[16px] leading-relaxed sm:text-[15px] ${
            isUser
              ? 'bg-wb-800 text-white shadow-sm'
              : 'bg-white text-slate-900 border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
          }`}
        >
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="field min-h-[60px] resize-y bg-white text-slate-900"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-ghost h-8 min-h-0 px-2 text-xs" onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary h-8 min-h-0 px-2 text-xs"
                  onClick={() => {
                    setEditing(false);
                    onEdit?.(draft);
                  }}
                >
                  Save and resend
                </button>
              </div>
            </div>
          ) : (
            // break-words: long unbroken tokens (URLs, IDs) must wrap inside
            // the bubble instead of dragging the chat pane into horizontal
            // scroll. GFM tables scroll inside their own wrapper for the
            // same reason — a wide table would otherwise shift every bubble
            // sideways on phones.
            <div className={`prose prose-sm max-w-none break-words ${isUser ? 'prose-invert' : ''}`}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={{
                  table: (props) => (
                    <div className="overflow-x-auto">
                      <table {...props} />
                    </div>
                  ),
                }}
              >
                {message.content || (isUser ? '' : '_(no response)_')}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {message.chartSpec && (
        <div className="mt-3 mx-1 sm:mx-12">
          <ChartRenderer spec={message.chartSpec} />
        </div>
      )}

      {!editing && (
        <div
          className={`mt-1 flex gap-1 text-xs text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 focus-within:opacity-100 motion-reduce:opacity-100 ${
            isUser ? 'justify-end' : 'justify-start'
          }`}
        >
          {onCopy && (
            <button
              type="button"
              onClick={handleCopy}
              className="btn-ghost h-7 min-h-0 px-2 text-[11px]"
              aria-label="Copy message"
              title="Copy to clipboard"
            >
              {justCopied ? '✓ Copied' : 'Copy'}
            </button>
          )}
          {isUser && onEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-ghost h-7 min-h-0 px-2 text-[11px]"
            >
              Edit
            </button>
          )}
          {!isUser && onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              className="btn-ghost h-7 min-h-0 px-2 text-[11px]"
            >
              Regenerate
            </button>
          )}
          {!isUser && onPin && (
            <button
              type="button"
              onClick={onPin}
              className="btn-ghost h-7 min-h-0 px-2 text-[11px]"
            >
              {pinned ? 'Unpin' : 'Pin conversation'}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="btn-ghost h-7 min-h-0 px-2 text-[11px] text-red-600 hover:bg-red-50"
              aria-label="Delete message"
              title="Delete this message"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </article>
  );
}
